import React, { useEffect, useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
} from "@mui/material";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import {
    deleteAllergy,
    getAllergys,
    getAllergysError,
    getAllergysStatus,
    getAllergysSuccess,
    updateStatus,
    selectAllAllergys
} from "../../features/allergySlice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import Loading from "../../components/loading";
import Error from "../../components/Error";
import AppBarSearch from "../../global/appBarSearch";
import NoDataTable from "../../components/noDataTable";
import ParagraphHighliter from "../../components/paragraphHighliter";
import RestaurantTopBar from "../../components/restaurantTopBar";
import { ResponsiveProvider } from "../../hooks/uiHook";
import { useNavigate } from "react-router-dom";
import AlertDialog from "../../components/alertDialog";
import { useTranslation } from "react-i18next";
import TableActions from "../../components/tableActions";
const AllergyManagement = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [localAllergys, setLocalAllergys] = useState([]);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [cardId, setCardId] = useState(null);
    const allergies = useSelector(selectAllAllergys);
    const error = useSelector(getAllergysError);
    const success = useSelector(getAllergysSuccess);
    const status = useSelector(getAllergysStatus);

    const handleClickOpen = (cardId) => {
        setOpen(true);
        setCardId(cardId);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        navigate("add");
    };

    const handleModify = (data) => {
        navigate("modify", { state: { allergy: data } });
    };

    useEffect(() => {
        if (status === "fetchData") setLocalAllergys(allergies);
    }, [allergies, status]);

    useEffect(() => {
        const alreadyLoaded = allergies.length > 0 && status === "fetchData";
        if (!alreadyLoaded) {
            dispatch(getAllergys());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const filteredAllergysMemo = useMemo(() => {
        let filtered = localAllergys || [];

        if (search) {
            filtered = filtered.filter((product) =>
                (product.name || "").toLowerCase().includes(search.toLowerCase())
            );
        }

        return filtered;
    }, [localAllergys, search]);

    useEffect(() => {
        if (status === "deleteSuccess") {
            toast.success(success);
            setLocalAllergys((prev) => prev.filter((item) => item._id !== cardId));
            dispatch(updateStatus());
        } else if (status === "deleteError") {
            toast.error(error);
        }
    }, [cardId, dispatch, error, status, success, filteredAllergysMemo]);

    return (
        <div className="main-application">
            <RestaurantTopBar />
            <ResponsiveProvider maxWidth={900}>
                <AppBarSearch
                    searchText={t("search.byName")}
                    handleSearch={(e) => setSearch(e.target.value)}
                    title={t("pages.allergies")}
                    buttonTitle={t("actions.addAllergy")}
                    handleSubmit={handleSubmit}
                />
            </ResponsiveProvider>
            <main>
                <Box sx={{ my: 2 }}>
                    <TableContainer>
                        <Table sx={{ backgroundColor: colors.primary[400] }}>
                            <TableHead sx={{ backgroundColor: colors.primary[700] }}>
                                <TableRow>
                                    <TableCell>{t("table.name")}</TableCell>
                                    <TableCell>{t("table.image")}</TableCell>
                                    <TableCell align="right">
                                        {t("table.actions")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {status === "loading" ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">
                                            <Loading size={8} />
                                        </TableCell>
                                    </TableRow>
                                ) : status === "fetchError" ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">
                                            <Error>{error}</Error>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredAllergysMemo && filteredAllergysMemo.length > 0 ? (
                                    filteredAllergysMemo.map((allergy, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <ParagraphHighliter
                                                    title={allergy.name}
                                                    search={search}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <img
                                                    src={`${process.env.REACT_APP_MEDIA_URL}/${allergy.icon}`}
                                                    alt={allergy.name}
                                                    style={{
                                                        width: "75px",
                                                        height: "75px",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableActions
                                                    actions={[
                                                        {
                                                            type: "edit",
                                                            color: "warning",
                                                            label: t("common.edit"),
                                                            onClick: () => handleModify(allergy),
                                                        },
                                                        {
                                                            type: "delete",
                                                            color: "error",
                                                            label: t("common.delete"),
                                                            onClick: () => handleClickOpen(allergy._id),
                                                        },
                                                    ]}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <NoDataTable cols={2} />
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </main>
            <AlertDialog
                open={open}
                handleClose={() => setOpen(false)}
                message={"Voulez-vous supprimer cette allergie ?"}
                cardId={cardId}
                deleteData={() => {
                    dispatch(deleteAllergy(cardId));
                    setOpen(false);
                }}
            />
        </div>
    );
};

export default AllergyManagement;
